import usePoolById from '@/hooks/queries/usePoolById';
import Bullet from '../ui/Bullet';
import Card from '../ui/Card';
import Flex from '../ui/Flex';
import { proxiesByType } from '@/model/proxyTypes';
import ProxyIcon from '@/assets/icons/ProxyIcon';
import { ReactNode } from 'react';
import FlagIcon from '@/assets/icons/FlagIcon';
import { countryByCode } from '@/model/static_countries';
import { proxyPoolRotationOptionsMap } from '@fd/common/biz/proxyPoolRotationOptions';
import { NavLink } from 'react-router-dom';
import { linkClassName } from '../ui/styles';
import GenerateProxies from './GenerateProxies';
import { DesktopView } from '@/components/responsive';

type PoolInfoProps = {
  poolId: number;
};

export default function PoolInfo({ poolId }: PoolInfoProps) {
  const { data: pool } = usePoolById(poolId);
  const proxyType = pool ? proxiesByType[pool?.proxy_type] : undefined;

  const generateCreatePoolRedirectLink = () => {
    if (!pool) return '/proxy-pool/create';
    let country = '';
    if (pool.countries) country = pool.countries[0];

    const cities = pool.cities.join(',');
    return `/proxy-pool/create?type=${pool.proxy_type}&country=${country}&cities=${cities}&rotation=${pool.rotation}`;
  };

  return (
    <Card className="w-full sm:max-w-[304px] p-5" isLoading={!pool}>
      {pool && proxyType && (
        <div className="flex flex-row flex-wrap sm:flex-col gap-4 sm:gap-5">
          <ProxyInfoField>
            Proxy type
            <Flex.Row className="gap-2 items-center">
              <ProxyIcon
                type={proxyType.type}
                className="h-5 w-5 stroke-icon-secondary"
              />
              <span>{proxyType.title}</span>
            </Flex.Row>
          </ProxyInfoField>
          <ProxyInfoField>
            Location
            <Flex.Row className="gap-2 items-center">
              {pool.countries && pool.countries.length > 0 && (
                <>
                  <FlagIcon
                    countryCode={pool.countries[0]}
                    className="rounded-[2px]"
                  />
                  <span>
                    {countryByCode.get(pool.countries[0])?.countryName}
                  </span>
                </>
              )}
            </Flex.Row>
          </ProxyInfoField>
          {pool.cities.length > 0 && (
            <Flex.Col className="gap-2 hidden sm:flex">
              <div className="text-sm font-medium">Cities</div>
              <Flex.Row className="gap-2 flex-wrap">
                {pool.cities.map((c) => (
                  <Bullet className="w-fit" key={c}>
                    {c}
                  </Bullet>
                ))}
              </Flex.Row>
            </Flex.Col>
          )}
          <ProxyInfoField>
            Rotation
            <Flex.Row className="gap-2 items-center">
              <span>{proxyPoolRotationOptionsMap.get(pool.rotation)}</span>
            </Flex.Row>
          </ProxyInfoField>
          <ProxyInfoField>
            Protocol
            <Flex.Row className="gap-2 items-center">
              <span>
                {pool.protocol.length > 0
                  ? pool.protocol.toUpperCase()
                  : 'HTTP'}
              </span>
            </Flex.Row>
          </ProxyInfoField>
          <DesktopView>
            <Flex.Col className="gap-2">
              <div className="text-sm font-medium">Add proxies</div>
              <GenerateProxies poolId={poolId} />
            </Flex.Col>
          </DesktopView>
          <div className="hidden sm:block">
            <span>Need adjustments?</span>
            <br />
            <NavLink
              to={generateCreatePoolRedirectLink()}
              className={linkClassName}
            >
              Just create a new proxy pool
            </NavLink>
          </div>
        </div>
      )}
    </Card>
  );
}

function ProxyInfoField({ children }: { children: [ReactNode, ReactNode] }) {
  return (
    <Flex.Col className="gap-2">
      <div className="text-sm font-medium hidden sm:block">{children[0]}</div>
      <Bullet className="w-fit sm:text-sm">{children[1]}</Bullet>
    </Flex.Col>
  );
}
